import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ReactFormExtendedApi } from '@tanstack/react-form';
import type { AnyFieldApi } from '@tanstack/form-core';

interface FieldRendererProps {
  field: AnyFieldApi;
  label: string;
  type?: string;
  disabled?: boolean;
}

function FieldRenderer({ field, label, type = 'text', disabled = false }: FieldRendererProps) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={field.name}
        className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400"
      >
        {label}
      </Label>
      <Input
        id={field.name}
        name={field.name}
        type={type}
        value={field.state.value}
        onBlur={field.handleBlur}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!field.state.meta.isValid && field.state.meta.errors.length > 0}
        className="rounded-none border-primary-200 dark:border-primary-800"
      />
      {field.state.meta.errors.length > 0 && (
        <p className="text-xs text-red-600 dark:text-red-400">
          {field.state.meta.errors.map((err: unknown) => typeof err === 'string' ? err : (err as { message?: string })?.message || String(err)).join(', ')}
        </p>
      )}
    </div>
  );
}

export function FormTextField<TFormData>({
  form,
  name,
  label,
  type = 'text',
  disabled = false,
}: {
  form: ReactFormExtendedApi<TFormData, any, any, any, any, any, any, any, any, any, any, any>;
  name: string;
  label: string;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <form.Field
      name={name}
      children={(field) => (
        <FieldRenderer field={field} label={label} type={type} disabled={disabled} />
      )}
    />
  );
}
